import { useRef } from "react";
import { useIntl } from "react-intl";
import { WmLineChart } from "@watermarkinsights/ripple-react";
import { AiDataCaptureCaption } from "../AiDataCaptureCaption";
import { DownloadActionMenu } from "./DownloadActionMenu";
import { useExportToPng } from "../export-conversation/useExportToPng";
import { buildLineChartCsvData, downloadTableAsCsv } from "../ai-chat-message-utils";
import type { LineChartPayload } from "../ai-chat-message-utils";

interface AiLineChartProps {
  payload: LineChartPayload;
  readOnly?: boolean;
}

export const AiLineChart = ({ payload, readOnly = false }: AiLineChartProps) => {
  const intl = useIntl();
  const {
    label,
    xAxisLabel,
    yAxisLabel,
    units,
    visibilityToggles,
    showDeltas,
    data,
  } = payload;
  const elementRef = useRef<HTMLDivElement>(null);
  const { isExporting, handleExport } = useExportToPng({
    elementRef,
    fileTitle: label || "Currents_Conversation_Chart",
    errorMessage: intl.formatMessage({ id: "app.toolboxChat.action.downloadPngError" }),
  });

  const lineDataGroupedBySeriesAndXValue = data.reduce<
    Record<string, Record<string, number | null>>
  >((accumulator, series) => {
    accumulator[series.label] = series.data.reduce<
      Record<string, number | null>
    >((pointsByXValue, point) => {
      pointsByXValue[point.x] = point.y;
      return pointsByXValue;
    }, {});
    return accumulator;
  }, {});

  // Stable key ensures WmLineChart remounts when data changes
  const stableChartKey = data
    .map(
      (series) =>
        `${series.label}:${series.data.map((p) => `${p.x}=${p.y}`).join(",")}`,
    )
    .join("|");

  const handleDownloadCsv = () => {
    const { columns, rows } = buildLineChartCsvData(payload);
    downloadTableAsCsv(columns, rows, label);
  };

  return (
    <div ref={elementRef}>
      <div className="tcw-chart-card">
        <div className="tcw-chart-header">
          {label && <p className="tcw-chart-label">{label}</p>}
          {!readOnly && (
            <DownloadActionMenu
              tooltip={intl.formatMessage({ id: "app.toolboxChat.action.downloadChart" })}
              onDownloadPng={handleExport}
              onDownloadCsv={handleDownloadCsv}
              disabled={isExporting}
            />
          )}
        </div>
        <div className="tcw-chart-wrapper tcw-chart-wrapper--line">
          <WmLineChart
            key={stableChartKey}
            label={label ?? "Line Chart"}
            lineData={JSON.stringify(lineDataGroupedBySeriesAndXValue)}
            xAxisLabel={xAxisLabel ?? ""}
            yAxisLabel={yAxisLabel ?? ""}
            units={units ?? ""}
            showGrid={true}
            visibilityToggles={
              String(visibilityToggles ?? data.length > 1) as unknown as boolean
            }
            showDeltas={String(showDeltas ?? false) as unknown as boolean}
          />
        </div>
        <AiDataCaptureCaption />
      </div>
    </div>
  );
};
